import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getOrdersByUserId } from '../services/orderService';
import { trackShipment } from '../services/shippingService';
import './OrdersPage.css';

const OrdersPage = () => {
  const { currentUser } = useAuth();
  const [userOrders, setUserOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(''); // eslint-disable-line no-unused-vars
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [trackingInfo, setTrackingInfo] = useState({});
  const [isTracking, setIsTracking] = useState({});

  // Sample order data for testing (using the structure you provided)
  const sampleOrders = [
    {
      id: "0ZjwmBZEooWFb6FbfOkv",
      orderNumber: "NXW1754206426521",
      createdAt: 1754206426522,
      shippingCharges: 0,
      paymentMethod: "COD",
      userId: "7uwy3sg7IYd2UP0RfmKjQxgOnqo1",
      courierName: "BlueDart Express",
      subtotal: 10169.49152542373,
      notes: "",
      shippingAddress: {
        state: "Karnataka",
        default: false,
        createdAt: 1754206415941,
        city: "Bangalore",
        userId: "7uwy3sg7IYd2UP0RfmKjQxgOnqo1",
        addressLine2: "",
        email: "",
        country: "India",
        addressLine1: "123 MG Road, Whitefield",
        phone: "7019371908",
        latitude: 0,
        name: "Syed Ahmed",
        landmark: "Near Forum Mall",
        pincode: "560050",
        longitude: 0,
        addressType: "Home",
        updatedAt: 1754206415941
      },
      totalAmount: 12000,
      gstAmount: 1830.5084745762713,
      paymentStatus: "PENDING",
      trackingNumber: "BD123456789IN",
      items: [
        {
          productName: "Dell Latitude E7440 | Refurbished",
          upgradePrice: 0,
          selectedRamUpgrade: null,
          productId: "IPnCGP7VQWUzlAIphHxY",
          selectedStorageUpgrade: null,
          price: 12000,
          totalPrice: 12000,
          quantity: 1,
          productImage: "https://firebasestorage.googleapis.com/v0/b/nexawave-4d18a.firebasestorage.app/o/product_images%2F15acb990-6fce-4577-87af-41445d1cd39e.jpg?alt=media&token=4cda02db-f4f3-4397-95a6-6d47f124716b"
        }
      ],
      updatedAt: 1754206426523,
      estimatedDeliveryDate: "2025-01-25",
      orderStatus: "SHIPPED",
      actualDeliveryDate: ""
    },
    {
      id: "1ZjwmBZEooWFb6FbfOkw",
      orderNumber: "NXW1754206426522",
      createdAt: 1754106426522,
      shippingCharges: 150,
      paymentMethod: "UPI",
      userId: "7uwy3sg7IYd2UP0RfmKjQxgOnqo1",
      courierName: "DTDC Express",
      subtotal: 8500,
      notes: "Handle with care",
      totalAmount: 8650,
      gstAmount: 1200,
      paymentStatus: "PAID",
      trackingNumber: "DT987654321IN",
      items: [
        {
          productName: "Gaming Mechanical Keyboard RGB",
          price: 4500,
          totalPrice: 4500,
          quantity: 1,
          productImage: "https://images.unsplash.com/photo-1541140532154-b024d705b90a?w=300"
        },
        {
          productName: "Wireless Gaming Mouse",
          price: 2000,
          totalPrice: 4000,
          quantity: 2,
          productImage: "https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=300"
        }
      ],
      orderStatus: "DELIVERED",
      estimatedDeliveryDate: "2025-01-20",
      actualDeliveryDate: "2025-01-18"
    },
    {
      id: "2ZjwmBZEooWFb6FbfOkx",
      orderNumber: "NXW1754206426523",
      createdAt: 1754006426522,
      shippingCharges: 0,
      paymentMethod: "Credit Card",
      userId: "7uwy3sg7IYd2UP0RfmKjQxgOnqo1",
      courierName: "",
      subtotal: 25000,
      totalAmount: 25000,
      gstAmount: 3750,
      paymentStatus: "PAID",
      trackingNumber: "",
      items: [
        {
          productName: "MacBook Air M2 | Refurbished",
          price: 25000,
          totalPrice: 25000,
          quantity: 1,
          productImage: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=300"
        }
      ],
      orderStatus: "PROCESSING",
      estimatedDeliveryDate: "2025-01-30"
    }
  ];

  useEffect(() => {
    fetchUserOrders();
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserOrders = async () => {
    setOrdersLoading(true);
    setOrdersError('');
    try {
      if (currentUser?.uid) {
        // Fetch real orders from Firestore using the new order service
        const orders = await getOrdersByUserId(currentUser.uid);
        if (orders.length > 0) {
          // Convert new order format to display format
          const formattedOrders = orders.map(order => ({
            id: order.id,
            orderNumber: order.orderId,
            createdAt: order.createdAt?.toDate ? order.createdAt.toDate().getTime() : Date.now(),
            shippingCharges: order.shippingCharges || 0,
            paymentMethod: order.paymentMethod || 'RAZORPAY',
            userId: order.userId,
            courierName: order.courierName || 'Standard Delivery',
            subtotal: order.subtotal,
            totalAmount: order.totalAmount,
            gstAmount: order.tax || 0,
            paymentStatus: order.paymentStatus,
            trackingNumber: order.trackingNumber || '',
            items: order.items.map(item => ({
              productName: item.name,
              productId: item.productId,
              price: item.price,
              totalPrice: item.price * item.quantity,
              quantity: item.quantity,
              productImage: item.imageUrls?.[0] || 'https://via.placeholder.com/300x200?text=No+Image'
            })),
            orderStatus: order.orderStatus,
            shippingAddress: order.shippingAddress,
            estimatedDeliveryDate: order.estimatedDeliveryDate || '',
            actualDeliveryDate: order.actualDeliveryDate || '',
            shiprocketShipmentId: order.shiprocketShipmentId || null
          }));
          setUserOrders(formattedOrders);
        } else {
          // If no real orders found, show sample data for testing
          setUserOrders(sampleOrders);
        }
      } else {
        // For non-authenticated users, show sample data
        setUserOrders(sampleOrders);
      }
    } catch (err) {
      console.error('Error fetching user orders:', err);
      setOrdersError('Failed to load orders. Showing sample data.');
      // Fallback to sample data on error
      setUserOrders(sampleOrders);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleTrackOrder = async (orderId, shipmentId) => {
    if (!shipmentId) return;
    
    // Toggle details if already fetched
    if (trackingInfo[orderId]) {
      setSelectedOrder(selectedOrder === orderId ? null : orderId);
      return;
    }

    setIsTracking(prev => ({ ...prev, [orderId]: true }));
    try {
      const response = await trackShipment(shipmentId);
      if (response && response.tracking_data) {
        setTrackingInfo(prev => ({ ...prev, [orderId]: response.tracking_data }));
      }
      setSelectedOrder(orderId);
    } catch (error) {
      console.error('Failed to track shipment:', error);
    } finally {
      setIsTracking(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'placed':
        return (
          <svg className="status-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        );
      case 'processing':
        return (
          <svg className="status-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2V6L16 2H12M6.5 4.5L4.5 6.5L8.5 10.5L10.5 8.5L6.5 4.5M12 18V22L8 18H12M17.5 19.5L19.5 17.5L15.5 13.5L13.5 15.5L17.5 19.5Z" />
          </svg>
        );
      case 'shipped':
        return (
          <svg className="status-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3,4A2,2 0 0,0 1,6V17H3A3,3 0 0,0 6,20A3,3 0 0,0 9,17H15A3,3 0 0,0 18,20A3,3 0 0,0 21,17H23V12L20,8H17V4M10,6L14,10L10,14V11H4V9H10M17,9.5H19.5L21.47,12H17V9.5Z" />
          </svg>
        );
      case 'delivered':
        return (
          <svg className="status-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
          </svg>
        );
      default:
        return (
          <svg className="status-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
          </svg>
        );
    }
  };

  const filteredOrders = filterStatus === 'all'
    ? userOrders
    : userOrders.filter(order => order.orderStatus.toLowerCase() === filterStatus);

  const orderStatusCounts = {
    all: userOrders.length,
    placed: userOrders.filter(o => o.orderStatus.toLowerCase() === 'placed').length,
    processing: userOrders.filter(o => o.orderStatus.toLowerCase() === 'processing').length,
    shipped: userOrders.filter(o => o.orderStatus.toLowerCase() === 'shipped').length,
    delivered: userOrders.filter(o => o.orderStatus.toLowerCase() === 'delivered').length
  };

  if (ordersLoading) {
    return (
      <div className="orders-page">
        <div className="orders-loading">
          <div className="loading-spinner-large"></div>
          <h3>Loading your orders...</h3>
          <p>Please wait while we fetch your order history</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page" style={{ paddingTop: '20px' }}>
      <div className="orders-container max-w-7xl mx-auto px-4 md:px-6 mb-10">
        
        {/* Cart-style Breadcrumbs and Title */}
        <div className="orders-breadcrumbs">
          <span>Home</span>
          <span className="separator">›</span>
          <span className="active">My Orders</span>
        </div>
        
        <h1 className="orders-main-title">My Orders</h1>

        <div className="orders-content">
          {/* Sleek Filter Tabs */}
          <div className="sleek-orders-filters">
            {[
              { key: 'all', label: 'All Orders', icon: '📦' },
              { key: 'placed', label: 'Placed', icon: '🆕' },
              { key: 'processing', label: 'Processing', icon: '⚙️' },
              { key: 'shipped', label: 'Shipped', icon: '🚚' },
              { key: 'delivered', label: 'Delivered', icon: '✅' }
            ].map(filter => (
              <button
                key={filter.key}
                className={`sleek-filter-tab ${filterStatus === filter.key ? 'active' : ''}`}
                onClick={() => setFilterStatus(filter.key)}
              >
                <span className="filter-icon">{filter.icon}</span>
                <span className="filter-label">{filter.label}</span>
                <span className="filter-count">{orderStatusCounts[filter.key]}</span>
              </button>
            ))}
          </div>

          {/* Orders List */}
          <div className="orders-list">
            {filteredOrders.length === 0 ? (
              <div className="no-orders">
                <div className="no-orders-icon">📦</div>
                <h3>No orders found</h3>
                <p>You haven't placed any orders in this category yet.</p>
                <button className="btn-primary">Start Shopping</button>
              </div>
            ) : (
              filteredOrders.map(order => (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <div className="order-info">
                      <div className="order-number">
                        <span className="label">Order</span>
                        <span className="value">#{order.orderNumber}</span>
                      </div>
                      <div className="order-date">
                        <span className="label">Placed on</span>
                        <span className="value">{new Date(order.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}</span>
                      </div>
                    </div>
                    <div className="order-status-badge">
                      {getStatusIcon(order.orderStatus)}
                      <span className={`status-text status-${order.orderStatus.toLowerCase()}`}>
                        {order.orderStatus}
                      </span>
                    </div>
                  </div>

                  <div className="order-items">
                    {order.items.map((item, index) => (
                      <div key={index} className="order-item">
                        <div className="item-image">
                          <img src={item.productImage} alt={item.productName} />
                          {item.quantity > 1 && <span className="quantity-badge">{item.quantity}</span>}
                        </div>
                        <div className="item-details">
                          <h4 className="item-name">{item.productName}</h4>
                          <p className="item-price">₹{item.totalPrice.toLocaleString()}</p>
                          {item.quantity > 1 && <p className="item-qty">Qty: {item.quantity}</p>}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="order-summary">
                    <div className="summary-row">
                      <span>Payment Method</span>
                      <span className="payment-method">{order.paymentMethod}</span>
                    </div>
                    <div className="summary-row">
                      <span>Payment Status</span>
                      <span className={`payment-status ${order.paymentStatus.toLowerCase()}`}>
                        {order.paymentStatus}
                      </span>
                    </div>
                    {order.trackingNumber && (
                      <div className="summary-row">
                        <span>Tracking</span>
                        <span className="tracking-number">{order.trackingNumber}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Total Amount</span>
                      <span className="total-amount">₹{order.totalAmount.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="order-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => setSelectedOrder(selectedOrder === order.id ? null : order.id)}
                    >
                      {selectedOrder === order.id ? 'Hide Details' : 'View Details'}
                    </button>
                    {order.shiprocketShipmentId && (
                      <button 
                        className="btn-primary"
                        onClick={() => handleTrackOrder(order.id, order.shiprocketShipmentId)}
                        disabled={isTracking[order.id]}
                      >
                        {isTracking[order.id] ? 'Loading...' : 'Track Real-Time'}
                      </button>
                    )}
                    {order.orderStatus.toLowerCase() === 'delivered' && (
                      <button className="btn-primary">Reorder</button>
                    )}
                  </div>

                  {/* Expanded Details */}
                  {selectedOrder === order.id && (
                    <div className="order-details-expanded">
                      
                      {/* Shiprocket Live Tracking Box */}
                      {trackingInfo[order.id] && (
                        <div className="detail-section live-tracking-section">
                          <h4>🚀 Live Tracking Update</h4>
                          {trackingInfo[order.id].shipment_track && trackingInfo[order.id].shipment_track.length > 0 ? (
                            <div className="realtime-tracking-box">
                              <div className="tracking-grid">
                                <div>
                                  <span className="label">Current Status</span>
                                  <span className="value status-highlight">{trackingInfo[order.id].shipment_track[0].current_status || 'Processing'}</span>
                                </div>
                                <div>
                                  <span className="label">Expected Delivery</span>
                                  <span className="value">{trackingInfo[order.id].shipment_track[0].expected_date || 'Pending'}</span>
                                </div>
                                <div>
                                  <span className="label">AWB Number</span>
                                  <span className="value">{trackingInfo[order.id].shipment_track[0].awb_code || 'Awaiting Generation'}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <p>Tracking has been initiated but carrier data is not yet fully available. Please check back later.</p>
                          )}
                        </div>
                      )}

                      {order.shippingAddress && (
                        <div className="detail-section">
                          <h4>Shipping Address</h4>
                          <div className="address">
                            <p className="name">{order.shippingAddress.name}</p>
                            <p>{order.shippingAddress.addressLine1}</p>
                            {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
                            <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.pincode}</p>
                            <p className="phone">📞 {order.shippingAddress.phone}</p>
                          </div>
                        </div>
                      )}

                      <div className="detail-section">
                        <h4>Order Timeline</h4>
                        <div className="timeline">
                          <div className={`timeline-item ${['placed', 'processing', 'shipped', 'delivered'].includes(order.orderStatus.toLowerCase()) ? 'completed' : ''}`}>
                            <div className="timeline-dot"></div>
                            <div className="timeline-content">
                              <h5>Order Placed</h5>
                              <p>{new Date(order.createdAt).toLocaleDateString()}</p>
                            </div>
                          </div>
                          {order.orderStatus.toLowerCase() !== 'placed' && (
                            <div className={`timeline-item ${['processing', 'shipped', 'delivered'].includes(order.orderStatus.toLowerCase()) ? 'completed' : ''}`}>
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <h5>Processing</h5>
                                <p>Order is being prepared</p>
                              </div>
                            </div>
                          )}
                          {['shipped', 'delivered'].includes(order.orderStatus.toLowerCase()) && (
                            <div className={`timeline-item ${order.orderStatus.toLowerCase() === 'delivered' ? 'completed' : 'active'}`}>
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <h5>Shipped</h5>
                                <p>Out for delivery</p>
                                {order.trackingNumber && <p className="tracking">Tracking: {order.trackingNumber}</p>}
                              </div>
                            </div>
                          )}
                          {order.orderStatus.toLowerCase() === 'delivered' && (
                            <div className="timeline-item completed">
                              <div className="timeline-dot"></div>
                              <div className="timeline-content">
                                <h5>Delivered</h5>
                                <p>{order.actualDeliveryDate ? new Date(order.actualDeliveryDate).toLocaleDateString() : 'Delivered successfully'}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4>Price Breakdown</h4>
                        <div className="price-breakdown">
                          <div className="price-row">
                            <span>Subtotal</span>
                            <span>₹{order.subtotal?.toLocaleString() || (order.totalAmount - (order.gstAmount || 0)).toLocaleString()}</span>
                          </div>
                          {order.gstAmount > 0 && (
                            <div className="price-row">
                              <span>GST</span>
                              <span>₹{order.gstAmount.toLocaleString()}</span>
                            </div>
                          )}
                          {order.shippingCharges > 0 && (
                            <div className="price-row">
                              <span>Shipping</span>
                              <span>₹{order.shippingCharges.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="price-row total">
                            <span>Total</span>
                            <span>₹{order.totalAmount.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
